import React, { useState } from 'react';

// GIF Demo component - loads from public/training-assets folder
const GifDemo = ({ filename, fallbackText }) => {
  const [hasError, setHasError] = useState(false);
  const gifPath = `/training-assets/${filename}`;

  if (hasError) {
    // Fallback when GIF doesn't exist
    return (
      <div style={{ 
        width: '120px', 
        height: '90px', 
        backgroundColor: '#f0f0f0', 
        borderRadius: '8px', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        border: '2px dashed #ccc',
        padding: '8px',
        textAlign: 'center'
      }}>
        <span style={{ fontSize: '0.7rem', color: '#666' }}>
          {fallbackText || 'Demo coming soon'}
        </span>
      </div>
    );
  }

  return (
    <img 
      src={gifPath}
      alt={fallbackText}
      onError={() => setHasError(true)}
      style={{ 
        width: '120px', 
        height: '90px', 
        objectFit: 'cover', 
        borderRadius: '8px',
        border: '1px solid #dee2e6'
      }}
    />
  );
};

const MouseGestures = () => {
  const sections = [
    {
      title: '🖱️ Mouse Actions',
      tips: [
        {
          action: 'Single Click',
          gif: 'single-click.gif',
          fallback: '👆 One tap on mouse button',
          uses: [
            'Select a row checkbox',
            'Open a menu or dropdown',
            'Click buttons and links',
            'Focus on an input field'
          ]
        },
        {
          action: 'Double Click',
          gif: 'double-click.gif',
          fallback: '👆👆 Two quick taps',
          uses: [
            'Edit a cell in tables (Material Index, MTO, etc.)',
            'Select a word in text',
            'Open items in some lists'
          ]
        },
        {
          action: 'Right Click',
          gif: 'right-click.gif',
          fallback: '🖱️ Click right button',
          uses: [
            'Open browser context menu',
            'Copy/Paste options',
            'Inspect element (for debugging)'
          ]
        },
        {
          action: 'Click & Drag',
          gif: 'click-drag.gif',
          fallback: '👆➡️ Hold and move',
          uses: [
            'Select text in cells',
            'Resize sidebar (drag the edge)',
            'Scroll horizontally in wide tables'
          ]
        }
      ]
    },
    {
      title: '📱 Trackpad Gestures (Laptop)',
      tips: [
        {
          action: 'Two-finger Scroll',
          gif: 'two-finger-scroll.gif',
          fallback: '✌️⬆️⬇️ Two fingers up/down',
          uses: [
            'Scroll up/down on pages',
            'Scroll inside tables and dropdowns',
            'Horizontal scroll in wide tables (shift + scroll on some systems)'
          ]
        },
        {
          action: 'Pinch to Zoom',
          gif: 'pinch-zoom.gif',
          fallback: '🤏 Pinch in/out',
          uses: [
            'Zoom in/out on the page',
            'Useful for viewing dense tables',
            'Reset with Ctrl + 0'
          ]
        },
        {
          action: 'Two-finger Swipe Left/Right',
          gif: 'two-finger-swipe.gif',
          fallback: '✌️⬅️➡️ Two fingers sideways',
          uses: [
            'Navigate back/forward in browser (Mac)',
            'Works like browser back button',
            'Alternative to Backspace shortcut'
          ]
        }
      ]
    },
    {
      title: '🔄 Scroll Wheel',
      tips: [
        {
          action: 'Scroll Up/Down',
          gif: 'scroll-wheel.gif',
          fallback: '🖱️⬆️⬇️ Roll wheel up/down',
          uses: [
            'Navigate through long pages',
            'Scroll through table rows',
            'Scroll through dropdown options'
          ]
        },
        {
          action: 'Shift + Scroll',
          gif: 'shift-scroll.gif',
          fallback: '⇧+🖱️ Hold Shift + scroll',
          uses: [
            'Horizontal scroll in wide tables',
            'Navigate left/right without dragging'
          ]
        },
        {
          action: 'Ctrl + Scroll',
          gif: 'ctrl-scroll.gif',
          fallback: '⌃+🖱️ Hold Ctrl + scroll',
          uses: [
            'Zoom in/out on the page',
            'Useful for accessibility',
            'Reset with Ctrl + 0'
          ]
        }
      ]
    }
  ];

  const tableNavigation = [
    { action: 'Scroll inside table', description: 'Tables have their own scroll area. Hover over the table and scroll to see more rows.' },
    { action: 'Horizontal scroll', description: 'Wide tables (like Material Index) scroll horizontally. Use the bottom scrollbar or Shift+Scroll.' },
    { action: 'Sticky columns', description: 'The checkbox and Actions columns stay fixed when scrolling horizontally.' },
    { action: 'Sticky header', description: 'Column headers stay visible when scrolling down through rows.' },
    { action: 'Filter dropdown', description: 'Click the 🔽 icon on any column header to open the filter. Press Esc or click outside to close.' },
    { action: 'Sort columns', description: 'Click on a column name to sort. Click again to reverse. Third click removes sort.' },
  ];

  return (
    <div style={{ padding: '2rem', maxWidth: '1000px', margin: '0 auto' }}>
      <div style={{ marginBottom: '2rem' }}>
        <h2 style={{ margin: 0, color: '#333', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          🖱️ Mouse & Trackpad Tips
        </h2>
        <p style={{ color: '#666', marginTop: '0.5rem' }}>
          Learn how to use your mouse and trackpad effectively to navigate LGL Portal faster.
        </p>
      </div>

      {/* Admin note for adding GIFs */}
      <div style={{ 
        backgroundColor: '#fff3cd', 
        padding: '1rem', 
        borderRadius: '8px', 
        marginBottom: '2rem',
        border: '1px solid #ffc107',
        fontSize: '0.85rem'
      }}>
        <strong>📁 Admin Note:</strong> To add GIF demonstrations, place files in: 
        <code style={{ backgroundColor: '#f8f9fa', padding: '2px 6px', borderRadius: '3px', marginLeft: '4px' }}>
          lgltools-react/public/training-assets/
        </code>
        <br />
        <span style={{ color: '#666', fontSize: '0.8rem' }}>
          Required files: single-click.gif, double-click.gif, right-click.gif, click-drag.gif, two-finger-scroll.gif, pinch-zoom.gif, two-finger-swipe.gif, scroll-wheel.gif, shift-scroll.gif, ctrl-scroll.gif
        </span>
      </div>

      {sections.map((section, idx) => (
        <div key={idx} style={{ marginBottom: '2.5rem' }}>
          <h3 style={{ 
            color: '#495057', 
            borderBottom: '2px solid #dee2e6', 
            paddingBottom: '0.5rem',
            marginBottom: '1rem'
          }}>
            {section.title}
          </h3>
          <div style={{ display: 'grid', gap: '1rem', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))' }}>
            {section.tips.map((tip, tipIdx) => (
              <div 
                key={tipIdx} 
                style={{ 
                  backgroundColor: '#f8f9fa',
                  borderRadius: '8px',
                  padding: '1rem',
                  border: '1px solid #dee2e6',
                  display: 'flex',
                  gap: '1rem'
                }}
              >
                {/* GIF Demo */}
                <div style={{ flexShrink: 0 }}>
                  <GifDemo filename={tip.gif} fallbackText={tip.fallback} />
                </div>
                {/* Content */}
                <div style={{ flex: 1 }}>
                  <div style={{ 
                    fontWeight: 'bold', 
                    color: '#333', 
                    marginBottom: '0.5rem',
                    fontSize: '1rem'
                  }}>
                    {tip.action}
                  </div>
                  <ul style={{ margin: 0, paddingLeft: '1.2rem', color: '#555', fontSize: '0.9rem' }}>
                    {tip.uses.map((use, useIdx) => (
                      <li key={useIdx} style={{ marginBottom: '0.2rem' }}>{use}</li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      <div style={{ marginBottom: '2rem' }}>
        <h3 style={{ 
          color: '#495057', 
          borderBottom: '2px solid #dee2e6', 
          paddingBottom: '0.5rem',
          marginBottom: '1rem'
        }}>
          📊 Table Navigation Tips
        </h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {tableNavigation.map((item, idx) => (
            <div 
              key={idx} 
              style={{ 
                display: 'flex',
                padding: '0.75rem 1rem',
                backgroundColor: idx % 2 === 0 ? '#f8f9fa' : 'white',
                borderRadius: '4px',
                border: '1px solid #eee',
                gap: '1rem'
              }}
            >
              <strong style={{ minWidth: '150px', color: '#333' }}>{item.action}</strong>
              <span style={{ color: '#555' }}>{item.description}</span>
            </div>
          ))}
        </div>
      </div>

      <div style={{ 
        backgroundColor: '#e7f3ff', 
        padding: '1rem', 
        borderRadius: '8px', 
        marginTop: '2rem',
        border: '1px solid #b3d7ff'
      }}>
        <strong>💡 Pro Tip:</strong> If a table feels cramped, try using <strong>Ctrl + Scroll</strong> to zoom out slightly, 
        or collapse the sidebar by clicking "◀ Collapse" to get more horizontal space.
      </div>
    </div>
  );
};

export default MouseGestures;
